import { ChangeEvent, forwardRef, ReactNode } from 'react';
import { Radio, RadioProps, Stack, Typography, useTheme } from '@mui/material';

import {
	getRadioSxProps,
	StyledIconWrapper,
	StyledRadioButtonCircle,
	StyledRadioButtonContainer,
	StyledRadioButtonInnerCircle,
	TRUNCATE_TEXT_SX_PROPS,
} from './MplRadioButton.styled';

interface MplRadioButtonProps extends Omit<RadioProps, 'icon' | 'checkedIcon'> {
	text: string;
	subtext?: string;
	icon?: ReactNode;
	disabled?: boolean;
	multiline?: boolean;
	dataTestId?: string;
}

export const MplRadioButton = forwardRef<
	HTMLButtonElement,
	MplRadioButtonProps
>(
	(
		{
			text,
			subtext,
			icon,
			disabled = false,
			multiline = false,
			dataTestId,
			...radioProps
		},
		ref
	) => {
		const theme = useTheme();

		/** FIXME this causes the onChange to be called twice */
		const handleClick = () => {
			if (!disabled && radioProps.onChange) {
				const syntheticEvent = {
					target: { checked: true },
				} as ChangeEvent<HTMLInputElement>;
				radioProps.onChange(syntheticEvent, true);
			}
		};

		return (
			<StyledRadioButtonContainer
				data-testid={dataTestId}
				disabled={disabled}
				checked={radioProps.checked}
				hasIcon={!!icon}
				multiline={multiline}
				onClick={handleClick}
			>
				{icon && (
					<StyledIconWrapper disabled={disabled}>{icon}</StyledIconWrapper>
				)}

				<Stack
					flex={1}
					minWidth={0}
					justifyContent="center"
					marginRight="4px"
					spacing={icon && subtext ? 0 : subtext ? 0.375 : 0}
				>
					<Typography
						variant="b2"
						color={theme.palette.primary.main}
						sx={multiline ? undefined : TRUNCATE_TEXT_SX_PROPS}
					>
						{text}
					</Typography>
					{subtext && (
						<Typography
							variant="l1"
							color={theme.palette.solutions?.[600]}
							sx={multiline ? undefined : TRUNCATE_TEXT_SX_PROPS}
						>
							{subtext}
						</Typography>
					)}
				</Stack>

				<Radio
					ref={ref}
					disabled={disabled}
					{...radioProps}
					sx={getRadioSxProps(theme)}
					icon={<StyledRadioButtonCircle disabled={disabled} />}
					checkedIcon={
						<StyledRadioButtonCircle disabled={disabled}>
							<StyledRadioButtonInnerCircle disabled={disabled} />
						</StyledRadioButtonCircle>
					}
				/>
			</StyledRadioButtonContainer>
		);
	}
);

MplRadioButton.displayName = 'MplRadioButton';
